import isEqual from 'lodash/isEqual';
import { cloneDeep } from '../libs/cloneDeep';
import type { TextDirection, TranscriptData, TranscriptWordData } from '../video-data/types';
import { Config } from '../video-data/config';
import type { DeepReadonlyObject } from '../types';
import { assertIsValidTranscriptData } from '../validators';
import type { BaseSettings } from './TimeAwareBase';
import { TimeInvariantBase } from './TimeInvariantBase';
import type { PlaybackRegion } from './PlaybackRegions';

/**
 * Transcript
 * The transcript contains word timing information.
 */
export class Transcript extends TimeInvariantBase {
    #words: TranscriptWordData[] = [];
    #language: string = '';
    #textDirection: TextDirection = Config.LEFT_TO_RIGHT_TEXT_DIRECTION;
    #separateWordsWithSpaces: boolean = true;

    constructor(settings: BaseSettings = { fetchResources: true }) {
        super(settings);
    }

    get empty() {
        return this.#words.length === 0;
    }

    /** returns basic transcript word statistics: minimum, maximum, mean and average word duration **/
    getWordDurationStats() {
        let min = Infinity;
        let max = -Infinity;
        let average = 0;
        const durations: number[] = [];
        this.words.forEach((word) => {
            const duration = word.endTime - word.startTime;
            if (min > duration) {
                min = duration;
            }
            if (max < duration) {
                max = duration;
            }
            average += duration;
            durations.push(duration);
        });
        average /= this.words.length;
        const mean = durations[Math.round((this.words.length - 1) / 2)];
        return { min, max, average, mean };
    }

    /**
     * duration (in milliseconds)
     */
    get duration(): number {
        if (this.#words.length === 0) {
            return 0;
        }
        const start = this.startTime;
        const end = this.endTime;

        if (start === undefined || end === undefined) {
            return 0;
        }
        return end - start;
    }

    get words(): DeepReadonlyObject<TranscriptWordData>[] {
        return this.#words;
    }

    get firstWord(): DeepReadonlyObject<TranscriptWordData> | undefined {
        return this.#words[0];
    }

    get lastWord(): DeepReadonlyObject<TranscriptWordData> | undefined {
        return this.#words.at(-1);
    }

    get text(): string {
        return this.#words
            .map((transcriptWordData) => transcriptWordData.word)
            .join(this.separateWordsWithSpaces ? ' ' : '');
    }

    get textDirection(): TextDirection {
        return this.#textDirection;
    }

    /**
     * Transcript language (ISO code)
     */
    get language(): string {
        return this.#language;
    }

    /**
     * start time (in milliseconds)
     */
    get startTime(): number | undefined {
        return this.#words[0]?.startTime;
    }

    get separateWordsWithSpaces(): boolean {
        return this.#separateWordsWithSpaces;
    }

    /**
     * end time (in milliseconds)
     */
    get endTime(): number | undefined {
        return this.#words.at(-1)?.endTime;
    }

    /**
     * Get active word index at time (in milliseconds).
     * A word is considered active when the given time is greater than or equal to the word's start time and
     * less than the next word's start time.
     */
    getActiveWordIndexAtTime(time: number): number {
        const start = this.startTime;
        const end = this.endTime;

        if (start === undefined || time < start) {
            return -1;
        }
        if (end === undefined || time > end) {
            return -1;
        }
        return this.#words.findIndex((word, index) => {
            if (index < this.#words.length - 1) {
                return word.startTime <= time && time < this.#words[index + 1].startTime;
            }
            return word.startTime <= time;
        });
    }

    /**
     * Get active word index at time (in milliseconds) while ensuring it is within the given delay. We want to hide the word if the delay is too long, unless it is the last word.
     */
    getActiveWordIndexAtTimeWithinThreshold(time: number, threshold: number): number {
        const start = this.startTime;
        if (start === undefined || time < start) {
            return -1;
        }
        const index = this.#words.findIndex((word, index) => {
            if (index < this.#words.length - 1) {
                return word.startTime <= time && time < this.#words[index + 1].startTime;
            }
            return word.startTime <= time;
        });
        if (index === -1) {
            return -1;
        }
        const currentThreshold = time - this.#words[index].endTime;
        if (currentThreshold > threshold) {
            return -1;
        }
        return index;
    }

    override get propertiesToSerialize(): string[] {
        return ['words', 'language', 'textDirection', 'separateWordsWithSpaces'];
    }

    set transcriptData(data: TranscriptData) {
        assertIsValidTranscriptData(data);

        if (isEqual(data, this.transcriptData)) {
            return;
        }
        this.#words = cloneDeep(data.words);
        this.#language = data.language;
        this.#textDirection = data.textDirection;
        this.#separateWordsWithSpaces = true;

        if (data.hasOwnProperty('separateWordsWithSpaces')) {
            this.#separateWordsWithSpaces = data.separateWordsWithSpaces;
        }
        this.requiresUpdate = true;
    }

    get transcriptData(): TranscriptData {
        return {
            words: this.#words,
            language: this.#language,
            textDirection: this.#textDirection,
            separateWordsWithSpaces: this.#separateWordsWithSpaces,
        };
    }

    _wordsCustomLoad(words: TranscriptWordData[]): void {
        this.#words = cloneDeep(words);
    }

    _wordsCustomSave(): TranscriptWordData[] {
        return cloneDeep(this.#words);
    }

    _languageCustomLoad(language: string): void {
        this.#language = language;
    }

    _textDirectionCustomLoad(textDirection: TextDirection): void {
        this.#textDirection = textDirection;
    }

    _separateWordsWithSpacesCustomLoad(separateWordsWithSpaces: boolean): void {
        this.#separateWordsWithSpaces = separateWordsWithSpaces;
    }

    override clear() {
        super.clear();
        this.#words = [];
        this.#language = '';
        this.#textDirection = Config.LEFT_TO_RIGHT_TEXT_DIRECTION;
        this.#separateWordsWithSpaces = true;
    }

    getDataForRange(startTime: number, endTime?: number): TranscriptData;
    getDataForRange(playbackRegions: Readonly<Readonly<PlaybackRegion>[]>): TranscriptData;
    getDataForRange(
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]> | number,
        endTime?: number,
    ): TranscriptData {
        if (typeof playbackRegions === 'number') {
            if (endTime === undefined) {
                throw new Error(
                    'Invalid arguments. If you supply a startTime, you must also supply an endTime.',
                );
            }
            playbackRegions = [[playbackRegions, endTime]];
        }
        return {
            words: this.getWordsWithinRange(playbackRegions),
            language: this.language,
            textDirection: this.textDirection,
            separateWordsWithSpaces: this.separateWordsWithSpaces,
        };
    }

    /**
     * Returns transcript data trimmed to the playback regions.
     */
    getTrimmedTranscriptData(
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    ): TranscriptData {
        return {
            words: this.getWordsWithinRange(playbackRegions),
            language: this.language,
            textDirection: this.textDirection,
            separateWordsWithSpaces: this.separateWordsWithSpaces,
        };
    }

    /**
     * Returns a new transcript trimmed to start and end time.
     */
    getTrimmedTranscript(playbackRegions: Readonly<Readonly<PlaybackRegion>[]>): Transcript {
        const transcriptData = this.getTrimmedTranscriptData(playbackRegions);
        const newTranscript = new Transcript({ fetchResources: true });
        newTranscript.transcriptData = transcriptData;
        return newTranscript;
    }

    /**
     * Return time ranges for the pause duration. If the duration between two words is greater than the pause duration, then a new time range is created.
     */
    getTimeRangesWithoutPauses(
        pauseDuration: number,
    ): Array<{ startTime: number; endTime: number }> {
        const timeRanges: Array<{ startTime: number; endTime: number }> = [];

        const start = this.startTime;
        if (start === undefined) {
            return timeRanges;
        }

        let startTime = start;
        let endTime = start;

        for (const word of this.words) {
            if (word.startTime > endTime + pauseDuration) {
                timeRanges.push({ startTime, endTime });
                startTime = word.startTime;
            }
            endTime = word.endTime;
        }

        timeRanges.push({ startTime, endTime });
        return timeRanges;
    }

    /**
     * Returns the words that are active at the given time and mirrors the transcript editing behaviour from Web. It's
     * crucial that this method is kept in sync. Otherwise, the transcript editing behaviour will be different between
     * Web (transcript editor) and Luminary (previews).
     * Only full words are included in this range, if a time boundary is partway through a word, it is not included.
     **/
    getWordsWithinRange(
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    ): TranscriptWordData[] {
        if (this.empty) {
            return [];
        }

        const returnVal: TranscriptWordData[] = [];
        const start = this.startTime;
        const end = this.endTime;

        if (start === undefined || end === undefined) {
            return [];
        }

        for (const [startTime, endTime] of playbackRegions) {
            if (startTime > end || endTime < start) {
                continue;
            }

            let startIndex = startTime < start ? 0 : this.getActiveWordIndexAtTime(startTime);

            // Skip if no valid index was found
            if (startIndex === -1) {
                continue;
            }

            const startWord = this.#words[startIndex];
            if (startWord && startWord.startTime < startTime) {
                // ensure that the start word is not partway through
                startIndex++;
            }

            let endIndex =
                endTime > end ? this.#words.length - 1 : this.getActiveWordIndexAtTime(endTime);

            // Skip if no valid index was found
            if (endIndex === -1) {
                continue;
            }

            const endWord = this.#words[endIndex];
            if (endWord && endWord.endTime > endTime) {
                // ensure that the end word is not partway through
                endIndex--;
            }

            // Ensure valid indices for slicing
            if (startIndex <= endIndex && startIndex >= 0 && endIndex < this.#words.length) {
                const words = this.#words.slice(startIndex, endIndex + 1);
                words.forEach((word) => {
                    returnVal.push(word);
                });
            }
        }

        return returnVal;
    }

    generateWordsFromTranscript(sourceWordReferenceIndexOffset = 0) {
        const words = this.words
            .filter((transcriptWordData) => transcriptWordData.word !== '')
            .flatMap((transcriptWordData) => transcriptWordData.word);

        let wordsWithSpaces = [];
        let sourceWordReferenceIndices = null;

        if (this.separateWordsWithSpaces) {
            // In English and other languages with spaces: add space after each word
            // and build sourceWordReferenceIndices according to these spaces.
            // create a list of indices that reference the word list and add -1 to indicate a space
            sourceWordReferenceIndices = new Array(words.length * 2);
            wordsWithSpaces = words.flatMap((word) => [word, ' ']);
            wordsWithSpaces.pop(); // remove the last space

            for (let i = 0; i < words.length; i++) {
                sourceWordReferenceIndices[i * 2] = i + sourceWordReferenceIndexOffset;
                sourceWordReferenceIndices[i * 2 + 1] = -1;
            }
        } else {
            // Some languages (Thai, Japanese, Javanese, Chinese, etc.)
            // don't have spaces between words.
            // In this case, sourceWordReferenceIndices a direct mapping of index to word.
            sourceWordReferenceIndices = new Array(words.length);
            wordsWithSpaces = words.flatMap((word) => [word]);
            for (let i = 0; i < words.length; i++) {
                sourceWordReferenceIndices[i] = i + sourceWordReferenceIndexOffset;
            }
        }

        return { wordsWithSpaces, sourceWordReferenceIndices };
    }
}
